import classNames from "classnames"
import type {
  ComponentPropsWithoutRef,
  Ref,
} from "react"
import {
  forwardRef,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react"

import styles from "./Slider.module.scss"
import { SliderContext } from "./SliderContext"

type Props = ComponentPropsWithoutRef<"div">

function numberSort(a: number, b: number): number {
  return a - b
}

/*
 * this container should be rendered around the SliderInput component
 * it provides a container for grid positioning and the SliderContext
 */
export const Slider = forwardRef<HTMLDivElement, Props>(
  function Slider(props: Props, propRef: Ref<HTMLDivElement>) {
    const [
      inputValues,
      setInputValues,
    ] = useState<number[]>([])

    const inputsRef = useRef<Record<string, number>>({})

    const [
      sliderDisabled,
      setSliderDisabled,
    ] = useState<boolean>(false)

    const addInput = useCallback(
      (id: string, value: number) => {
        inputsRef.current[id] = value
        setInputValues(Object.values(inputsRef.current).sort(numberSort))
      },
      [
        inputsRef,
        setInputValues,
      ],
    )

    const removeInput = useCallback(
      (id: string) => {
        delete inputsRef.current[id]
        setInputValues(Object.values(inputsRef.current).sort(numberSort))
      },
      [
        inputsRef,
        setInputValues,
      ],
    )

    const value = useMemo(
      () => ({
        inputValues,
        addInput,
        removeInput,
        setSliderDisabled,
      }),
      [
        inputValues,
        addInput,
        removeInput,
        setSliderDisabled,
      ],
    )

    const className = classNames(
      props.className,
      styles.slider,
      {
        [styles.sliderDisabled]: sliderDisabled,
      },
    )

    return (
      <SliderContext.Provider value={value}>
        <div
          {...props}
          className={className}
          ref={propRef}
        />
      </SliderContext.Provider>
    )
  },
)
